import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalizeMessage, Table, Button, InlineNotification, Select } from 'libs.nucleus';
import { AccessIcfPrompt } from '../components/access_icf_prompt/access_icf_prompt.component';
import { DocumentModal } from '../document_modal';
import { SITE_DOCUMENTS_PINNED_COLUMN_IDS, useSiteDocumentsTableColumns, } from './hooks/use_site_documents_table_columns.hook';
import { useAccessIcfPrompt } from '../hooks/use_access_icf_prompt.hook';
import { usePtsDocumentAction } from '../participant_documents/hooks/use_pts_document_action.hook';
import { DOCUMENT_STATUSES } from '../types/signed_document.types';
import { statusLabelMap } from '../utils/signed_document_status.helpers';
import { SITE_DOCUMENTS_PAGE_SIZE, useSiteDocuments } from './hooks/use_site_documents.hook';
import { isAwaitingSiteUserSignature, SITE_DOCUMENTS_SEARCH_MAX_LENGTH } from './site_documents.helpers';
const SITE_DOCUMENTS_SEARCH_DEBOUNCE_MS = 300;
export const SiteDocumentsView = ({ accountId, siteId, apiClient, getApiErrorMessage, isNoPiiSite, accountEmail, accountDisplayName, canOpenDocument = true, canDownloadConsentPdf = true, requiresIcfAccessWarning = false, onOpenDocumentDetails, getDocumentDetailsHref, initialPageIndex, onPageIndexChange, }) => {
    const translate = useLocalizeMessage();
    const [selectedDocument, setSelectedDocument] = useState(null);
    const { requestOpen, isPromptOpen, onConfirm, onCancel, onClose } = useAccessIcfPrompt(requiresIcfAccessWarning);
    const openDocument = useCallback((document) => {
        requestOpen(() => setSelectedDocument(document));
    }, [requestOpen]);
    const closeDocumentModal = useCallback(() => {
        setSelectedDocument(null);
    }, []);
    const { openPtsDocument, ptsDocumentModal } = usePtsDocumentAction({
        apiClient,
        accountId,
        isNoPiiSite,
        isDocGenEconsentEnabled: true,
        getApiErrorMessage,
        canDownloadConsentPdf,
    });
    const requestOpenPtsDocument = useCallback((document) => {
        requestOpen(() => openPtsDocument(document));
    }, [openPtsDocument, requestOpen]);
    const { siteDocumentsColumns } = useSiteDocumentsTableColumns({
        onOpenDocument: openDocument,
        onOpenPtsDocument: requestOpenPtsDocument,
        canOpenDocument,
        requiresIcfAccessWarning,
        onOpenDocumentDetails,
        getDocumentDetailsHref,
    });
    const [awaitingMySignature, setAwaitingMySignature] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [sorting, setSorting] = useState([]);
    const searchTimeoutRef = useRef();
    useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);
    const { documents, isLoading, isFetching, isError, isSearchIncomplete, retrySearchIndex, totalPages, pagination, setPagination, } = useSiteDocuments({
        accountId,
        siteId,
        apiClient,
        getApiErrorMessage,
        awaitingMySignature,
        statuses,
        searchValue,
        sorting,
        initialPage: initialPageIndex,
    });
    const onPageIndexChangeRef = useRef(onPageIndexChange);
    onPageIndexChangeRef.current = onPageIndexChange;
    useEffect(() => {
        onPageIndexChangeRef.current?.(pagination.pageIndex);
    }, [pagination.pageIndex]);
    // A restored ?page= can point past the end. Cortex reports hasMore, not a last-page index, so an
    // empty page after the first falls back to the first page. Wait for the fetch to settle, because
    // keepPreviousData shows the previous page's rows while the next one loads.
    const isPastLastPage = !isFetching && !isError && pagination.pageIndex > 0 && documents.length === 0;
    useEffect(() => {
        if (isPastLastPage) {
            setPagination((current) => ({ ...current, pageIndex: 0 }));
        }
    }, [isPastLastPage, setPagination]);
    const resetToFirstPage = useCallback(() => {
        setPagination((current) => ({ ...current, pageIndex: 0 }));
    }, [setPagination]);
    const onSearch = useCallback((value) => {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setSearchValue((value ?? '').slice(0, SITE_DOCUMENTS_SEARCH_MAX_LENGTH));
            resetToFirstPage();
        }, SITE_DOCUMENTS_SEARCH_DEBOUNCE_MS);
    }, [resetToFirstPage]);
    const onSearchClear = useCallback(() => {
        clearTimeout(searchTimeoutRef.current);
        setSearchValue('');
        resetToFirstPage();
    }, [resetToFirstPage]);
    const onSortingChange = useCallback((updater) => {
        setSorting((current) => (typeof updater === 'function' ? updater(current) : updater));
        resetToFirstPage();
    }, [resetToFirstPage]);
    const statusOptions = useMemo(() => DOCUMENT_STATUSES.map((status) => ({ value: status, label: translate(statusLabelMap[status]) })), [translate]);
    const selectedStatusOptions = useMemo(() => statusOptions.filter((option) => statuses.some((status) => status === option.value)), [statusOptions, statuses]);
    const onStatusesChange = useCallback((options) => {
        setStatuses(DOCUMENT_STATUSES.filter((status) => options.some((option) => option.value === status)));
        resetToFirstPage();
    }, [resetToFirstPage]);
    const awaitingSiteUserRowIds = useMemo(() => documents.filter(isAwaitingSiteUserSignature).map((document) => document._id), [documents]);
    const labelNoResults = useMemo(() => {
        if (!canOpenDocument) {
            return translate('error.participant_documents.unavailable_for_role');
        }
        if (isError) {
            return translate('error.site_documents.load_failed');
        }
        const hasSearch = searchValue.trim().length > 0;
        const statusCount = statuses.length;
        if (statusCount > 0 && awaitingMySignature && hasSearch) {
            return translate('label.site_documents.empty_status_and_awaiting_search', { count: statusCount });
        }
        if (statusCount > 0 && awaitingMySignature) {
            return translate('label.site_documents.empty_status_and_awaiting', { count: statusCount });
        }
        if (statusCount > 0 && hasSearch) {
            return translate('label.site_documents.empty_status_search', { count: statusCount });
        }
        if (statusCount > 0) {
            return translate('label.site_documents.empty_status_filter', { count: statusCount });
        }
        if (awaitingMySignature && hasSearch) {
            return translate('label.site_documents.empty_awaiting_search');
        }
        if (awaitingMySignature) {
            return translate('label.site_documents.empty_awaiting_my_signature');
        }
        if (hasSearch) {
            return translate('label.site_documents.empty_search');
        }
        return translate('label.site_documents.empty');
    }, [canOpenDocument, isError, statuses.length, awaitingMySignature, searchValue, translate]);
    return (_jsxs(_Fragment, { children: [isSearchIncomplete && (_jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-items-start nucleus-gap-4 nucleus-mb-4', children: [_jsx(InlineNotification, { type: 'error', title: '', subtitle: translate('error.site_documents.search_incomplete') }), _jsx(Button, { label: translate('generic.retry'), onClick: retrySearchIndex, dataTestId: 'site-documents-retry-search-index' })] })), _jsx(Table, { customHeaderContentPlacement: 'start', customHeaderContent: _jsxs("div", { className: 'nucleus-flex nucleus-flex-wrap nucleus-items-center nucleus-gap-2', children: [_jsx(Button, { type: 'button', variant: 'secondary', size: 'sm', label: translate('label.site_documents.filter_awaiting_my_signature'), dataTestId: 'site-documents-awaiting-my-signature', isActive: awaitingMySignature, "aria-pressed": awaitingMySignature, onClick: () => {
                                setAwaitingMySignature((isAwaitingMySignature) => !isAwaitingMySignature);
                                resetToFirstPage();
                            } }), _jsx(Select, { multiple: true, "aria-label": translate('label.site_documents.filter_status'), "data-active": selectedStatusOptions.length > 0, options: statusOptions, value: selectedStatusOptions, onChange: onStatusesChange, placeholder: translate('label.site_documents.filter_status'), selectionLabel: selectedStatusOptions.length > 0
                                ? translate('label.site_documents.filter_status_selected', {
                                    statuses: selectedStatusOptions.map((option) => option.label).join(', '),
                                })
                                : undefined, showMultipleSelectionBadges: false, width: 'sm', dataTestId: 'site-documents-status-filter' })] }), columns: siteDocumentsColumns, data: documents, getRowId: '_id', hasPagination: true, manualPagination: true, manualPaginationState: pagination, onPaginationChange: setPagination, pageCount: totalPages, initialPageSize: SITE_DOCUMENTS_PAGE_SIZE, initialColumnPinning: { left: [...SITE_DOCUMENTS_PINNED_COLUMN_IDS] }, 
                // Skeleton rows only for the first load: on a sort, page or search refetch they replaced
                // the previous rows and the table collapsed and re-measured its columns.
                isLoading: isLoading, labelNoResults: labelNoResults, paginationNavigationScreenReaderLabel: translate('info.site_documents.table_navigation'), highlightedRowIds: awaitingSiteUserRowIds, useInternalSearch: true, 
                // Search and sort run in Cortex, not over the loaded page — otherwise
                // both only ever see the 25 rows currently on screen.
                manualFiltering: true, onSearch: onSearch, onSearchClear: onSearchClear, 
                // Table's default is the English literal 'Search', used for both the placeholder and the
                // field's accessible name, so without this the search box stays English on a localized page.
                labelSearchField: translate('generic.search'), enableSorting: true, manualSorting: true, manualSortingState: sorting, onSortingChange: onSortingChange }), ptsDocumentModal, _jsx(AccessIcfPrompt, { isPromptOpen: isPromptOpen, onConfirm: onConfirm, onCancel: onCancel, onClose: onClose }), _jsx(DocumentModal, { document: selectedDocument, isOpen: !!selectedDocument, onClose: closeDocumentModal, accountId: accountId, accountEmail: accountEmail, accountDisplayName: accountDisplayName, apiClient: apiClient, getApiErrorMessage: getApiErrorMessage, isDocGenEconsentEnabled: true, canDownloadConsentPdf: canDownloadConsentPdf })] }));
};
